import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { IAdSettingsFormValues } from '@/editor/shared/components/forms/ad-settings/AdSettingsForm';
import type { RootState } from '@/store/store';

export interface IAdSetStepSlice {
  adSet: {
    title: string;
    startTime: string;
    endTime: string;
    budgetId: string | null;
    productId?: string | null;
    budgetValue?: number;
  };
  places: ICircleStoreModel[];
  valuesUpdated: boolean;
  valuesFetchedFromLocalStorage: boolean;
}

export interface ICircleStoreModel {
  address: string;
  radius: number;
  center: google.maps.LatLngLiteral;
  countryCode: string | null;
}

const initialState: IAdSetStepSlice = {
  adSet: {
    title: '',
    startTime: '',
    endTime: '',
    budgetId: '',
    productId: '',
    budgetValue: 0,
  },
  places: [],
  valuesUpdated: false,
  valuesFetchedFromLocalStorage: false,
};

const adSetStepSlice = createSlice({
  name: 'adSet-step',
  initialState: initialState,
  reducers: {
    setAdSetStepValues(
      state: IAdSetStepSlice,
      action: PayloadAction<{
        adSet: Pick<
          IAdSettingsFormValues,
          'title' | 'startTime' | 'endTime' | 'budgetId' | 'productId' | 'budgetValue'
        >;
        circles: ICircleStoreModel[];
        valuesUpdated: boolean;
        valuesFetchedFromLocalStorage: boolean;
      }>
    ) {
      state.adSet = action.payload.adSet;
      state.places = action.payload.circles;
      state.valuesUpdated = action.payload.valuesUpdated;
      state.valuesFetchedFromLocalStorage = action.payload.valuesFetchedFromLocalStorage;
    },
    addPlace(state: IAdSetStepSlice, action: PayloadAction<ICircleStoreModel>) {
      state.places.push(action.payload);
    },
    updatePlaceCenter(
      state: IAdSetStepSlice,
      action: PayloadAction<{ address: string; center: google.maps.LatLngLiteral }>
    ) {
      const { address, center } = action.payload;
      const circleIndex = state.places.findIndex((circle) => circle.address === address);

      if (!center) {
        console.error(`Center is undefined`);
        return;
      }

      if (circleIndex === -1) {
        console.error(`Circle with address ${address} not found`);
        return;
      }

      state.places[circleIndex].center = center;
    },
    updatePlaceRadius(state: IAdSetStepSlice, action: PayloadAction<{ address: string; radius: number }>) {
      const { address, radius } = action.payload;
      const circleIndex = state.places.findIndex((circle) => circle.address === address);

      if (!radius) {
        console.error(`Radius is undefined`);
        return;
      }

      if (circleIndex === -1) {
        console.error(`Circle with address ${address} not found`);
        return;
      }

      state.places[circleIndex].radius = radius;
    },
    removePlace(state: IAdSetStepSlice, action: PayloadAction<number>) {
      state.places.splice(action.payload, 1);
    },
    setPlaceArray(state: IAdSetStepSlice, action: PayloadAction<ICircleStoreModel[]>) {
      state.places = action.payload;
    },
    setAdsetValuesUpdated(state: IAdSetStepSlice, action: PayloadAction<boolean>) {
      state.valuesUpdated = action.payload;
    },
    setValuesFetchedFromStoreFlag(state: IAdSetStepSlice, action: PayloadAction<boolean>) {
      state.valuesFetchedFromLocalStorage = action.payload;
    },
    removeAllPlaces(state: IAdSetStepSlice) {
      state.places = [];
    },
  },
});

export const {
  setAdSetStepValues,
  addPlace,
  updatePlaceCenter,
  updatePlaceRadius,
  removePlace,
  setPlaceArray,
  setAdsetValuesUpdated,
  setValuesFetchedFromStoreFlag,
  removeAllPlaces,
} = adSetStepSlice.actions;

export default adSetStepSlice.reducer;

export const selectPlaces = (state: RootState) => state.adSetStepSliceReducer.places;
